import React, { ComponentType, useCallback, useState } from "react";

import cn from "classnames";
import isFunction from "lodash-es/isFunction";

import { isDefined } from "@mapmycustomers/shared/util/assert";
import { Modal, ModalProps } from "@mapmycustomers/ui";

import IWizardStep from "@app/component/wizard/types/IWizardStep";

import styles from "./Wizard.module.scss";

export interface WizardProps<T> extends Pick<ModalProps, "className" | "width" | "zIndex"> {
  initialState: T;
  initialStep?: number;
  onFinish?: (finalState: T) => void;
  onHide: (finished: boolean) => void;
  steps: ComponentType<IWizardStep<T>>[];
}

const Wizard = <T,>({
  className,
  initialState,
  initialStep,
  onFinish,
  onHide,
  steps,
  ...modalProps
}: WizardProps<T>) => {
  const [state, setState] = useState(initialState);
  const [currentStepIndex, setCurrentStepIndex] = useState(
    isDefined(initialStep) && initialStep >= 0 && initialStep < steps.length ? initialStep : 0
  );

  const handleCancel = useCallback(() => {
    onHide(false);
  }, [onHide]);

  const handleFinish = useCallback(() => {
    setState((state) => {
      // calling onFinish within setState to guarantee the latest state value
      // calling onHide here too because otherwise React may drop processing remaining
      // setState calls and onFinish will not be called.
      onFinish?.(state);
      onHide(true);
      return state;
    });
  }, [onHide, onFinish]);

  const handleGoBack = useCallback(() => {
    setCurrentStepIndex((index) => (index > 0 ? index - 1 : index));
  }, []);

  const handleGoForward = useCallback(() => {
    setCurrentStepIndex((index) => (index < steps.length - 1 ? index + 1 : index));
  }, [steps.length]);

  const handleGoToStep = useCallback(
    (newIndex: number) => {
      setCurrentStepIndex((index) => (newIndex >= 0 && newIndex < steps.length ? newIndex : index));
    },
    [steps.length]
  );

  const handleSetState = useCallback((state: ((prevState: T) => T) | Partial<T>) => {
    if (isFunction(state)) {
      setState(state);
    } else {
      setState((prevState) => ({ ...prevState, ...state }));
    }
  }, []);

  if (!steps.length) {
    return null;
  }

  const CurrentStepComponent = steps[currentStepIndex];

  return (
    <Modal
      {...modalProps}
      className={cn(styles.container, className)}
      closable={false}
      footer={null}
      onCancel={handleCancel}
      open
      title={null}
    >
      <CurrentStepComponent
        onCancel={handleCancel}
        onFinish={handleFinish}
        onGoBack={handleGoBack}
        onGoForward={handleGoForward}
        onGoToStep={handleGoToStep}
        onSetState={handleSetState}
        state={state}
      />
    </Modal>
  );
};

export default Wizard;
